import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, Play, X, ArrowDown, Zap, ListFilter } from 'lucide-react';
import api from '../../utils/api';
import { Button, Field, Input, Select, Switch, errMsg } from '../SupportCoordination/kit';

// Rules (what happens to new mail as it arrives) and quick steps (several actions in one click),
// both each person's own. A rule never forwards mail out of the company.
const Note = ({ children }) => <p className="text-[12.5px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2.5 leading-relaxed">{children}</p>;
const Empty = ({ children }) => <p className="text-[13px] text-slate-500 py-6 text-center">{children}</p>;
const IconAct = ({ label, icon: Icon, onClick, danger }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} className={clsx('w-8 h-8 rounded-md flex items-center justify-center', danger ? 'text-slate-500 hover:bg-rose-50 hover:text-rose-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800')}><Icon size={15} aria-hidden /></button>
);

// Folders of the mailboxes this person can act on, as select options ("mailbox › folder").
const folderOptions = (accounts) => (accounts || []).filter((a) => a.can_act).flatMap((a) => a.folders
  .filter((f) => !['drafts', 'sent'].includes(f.role))
  .map((f) => ({ value: f.id, label: `${(accounts || []).filter((x) => x.can_act).length > 1 ? `${a.email} › ` : ''}${f.path && f.path !== f.name ? f.path : f.name}`, account: a.id })));
const folderName = (accounts, id) => { const o = folderOptions(accounts).find((x) => x.value === id); return o ? o.label : 'a folder'; };
const tagName = (tags, id) => ((tags || []).find((t) => t.id === id) || {}).name || 'a tag';

function describeRule(r, accounts, tags) {
  const c = r.conditions || {};
  const when = [c.from && `from ${c.from}`, c.subjectHas && `subject has "${c.subjectHas}"`, c.bodyHas && `text has "${c.bodyHas}"`, c.toHas && `sent to ${c.toHas}`, c.hasAttachments && 'has attachments'].filter(Boolean);
  const a = r.actions || {};
  const does = [a.delete ? 'delete it' : a.moveTo && `move to ${folderName(accounts, a.moveTo)}`, a.tagId && `tag ${tagName(tags, a.tagId)}`, a.markRead && 'mark read', a.flag && 'flag'].filter(Boolean);
  return `When mail is ${when.join(' and ')}: ${does.join(', ')}`;
}
const toForm = (r) => ({ id: r.id, name: r.name, conditions: { ...(r.conditions || {}) }, actions: { ...(r.actions || {}) }, stopHere: !!(r.stop_here ?? r.stopHere), enabled: r.enabled !== false, accountId: r.account_id ?? r.accountId ?? null });

export function RulesSection({ accounts: all, tags, draft, me }) {
  // Rules run on new mail in mailboxes you own (a colleague's or a shared one runs its owner's rules).
  const accounts = (all || []).filter((a) => a.can_act && (!me || !a.owner_user_id || String(a.owner_user_id) === String(me.id)));
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(draft ? toForm(draft) : null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get('/mail/rules').then((r) => setRules(r.data || [])).catch((e) => { setRules([]); toast.error(errMsg(e)); }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (draft) setForm(toForm(draft)); }, [draft]);
  const body = (f) => ({ name: f.name, conditions: f.conditions, actions: f.actions, stopHere: f.stopHere, enabled: f.enabled, accountId: f.accountId || null });
  const save = async () => {
    setBusy('save');
    try {
      if (form.id) await api.put(`/mail/rules/${form.id}`, body(form)); else await api.post('/mail/rules', body(form));
      toast.success('Rule saved. It runs on new mail as it arrives.'); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const toggle = async (r, on) => {
    setRules((xs) => xs.map((x) => (x.id === r.id ? { ...x, enabled: on } : x)));
    try { await api.put(`/mail/rules/${r.id}`, body({ ...toForm(r), enabled: on })); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const runNow = async (r) => {
    setBusy(r.id);
    try { const x = await api.post(`/mail/rules/${r.id}/run`); toast.success(x.data.matched ? `Done on ${x.data.matched} message${x.data.matched === 1 ? '' : 's'} in the Inbox` : 'Nothing in the Inbox matches it'); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove the rule "${r.name}"?`)) return;
    try { await api.delete(`/mail/rules/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Note>Rules act on new mail as it arrives in the mailboxes you own, in this order. They stay in Rostery: the mailbox's own rules (in Outlook or Gmail) still run as well. A rule cannot forward mail.</Note>
      <div className="mt-4 flex justify-end"><Button size="sm" icon={Plus} onClick={() => setForm({ name: '', conditions: {}, actions: {}, stopHere: false, enabled: true, accountId: null })}>New rule</Button></div>
      {form && <RuleForm form={form} setForm={setForm} onSave={save} saving={busy === 'save'} accounts={accounts} tags={tags} />}
      {rules === null ? <Empty>Loading…</Empty> : rules.length ? (
        <ul className="mt-3 rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
          {rules.map((r) => (
            <li key={r.id} className="flex items-start gap-3 px-3 py-2.5">
              <ListFilter size={15} className="text-slate-400 mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className={clsx('text-[13.5px] font-semibold', r.enabled === false ? 'text-slate-400' : 'text-slate-800')}>{r.name}</p>
                <p className="text-[12px] text-slate-500">{describeRule(r, accounts, tags)}{r.stop_here ? '. Then stop.' : ''}</p>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Switch label={<span className="sr-only">On</span>} checked={r.enabled !== false} onChange={(v) => toggle(r, v)} />
                <IconAct label="Run now on the Inbox" icon={Play} onClick={() => runNow(r)} />
                <IconAct label="Change" icon={Pencil} onClick={() => setForm(toForm(r))} />
                <IconAct label="Remove" icon={Trash2} danger onClick={() => remove(r)} />
              </div>
            </li>
          ))}
        </ul>
      ) : !form && <Empty>No rules yet. For example: mail from the NDIA goes to a folder and is tagged "NDIA".</Empty>}
    </>
  );
}

function RuleForm({ form, setForm, onSave, saving, accounts, tags }) {
  const c = form.conditions; const a = form.actions;
  const setC = (k, v) => setForm({ ...form, conditions: { ...c, [k]: v } });
  const setA = (patch) => setForm({ ...form, actions: { ...a, ...patch } });
  const folders = folderOptions(accounts);
  const mine = (accounts || []).filter((x) => x.can_act);
  return (
    <form className="mt-3 rounded-xl ring-1 ring-sky-200 bg-sky-50/40 p-4 space-y-4" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
      <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} autoFocus placeholder="e.g. NDIA mail" /></Field>
      <fieldset className="space-y-3">
        <legend className="text-[13px] font-semibold text-slate-800 mb-1">When new mail…</legend>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Is from" hint="An address, or a domain such as ndis.gov.au"><Input value={c.from || ''} onChange={(e) => setC('from', e.target.value)} /></Field>
          <Field label="Is sent to or copied to"><Input value={c.toHas || ''} onChange={(e) => setC('toHas', e.target.value)} /></Field>
          <Field label="Subject has the words"><Input value={c.subjectHas || ''} onChange={(e) => setC('subjectHas', e.target.value)} /></Field>
          <Field label="Message has the words"><Input value={c.bodyHas || ''} onChange={(e) => setC('bodyHas', e.target.value)} /></Field>
        </div>
        <Switch label="Has attachments" checked={!!c.hasAttachments} onChange={(v) => setC('hasAttachments', v || undefined)} />
        {mine.length > 1 && (
          <Field label="In"><Select value={form.accountId || ''} allowEmpty emptyLabel="All my mailboxes" onChange={(v) => setForm({ ...form, accountId: v || null, actions: { ...a, moveTo: undefined } })}
            options={mine.map((x) => ({ value: x.id, label: x.email }))} /></Field>
        )}
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="text-[13px] font-semibold text-slate-800 mb-1">Do this</legend>
        <Switch label="Delete it" hint="To Deleted items." checked={!!a.delete} onChange={(v) => setA({ delete: v || undefined, moveTo: v ? undefined : a.moveTo })} />
        {!a.delete && (
          <Field label="Move it to"><Select value={a.moveTo || ''} allowEmpty emptyLabel="Leave it where it is"
            onChange={(v) => { const o = folders.find((x) => x.value === v); setForm({ ...form, accountId: v && o ? o.account : form.accountId, actions: { ...a, moveTo: v || undefined } }); }}
            options={folders.filter((o) => !form.accountId || o.account === form.accountId)} /></Field>
        )}
        <Field label="Tag it"><Select value={a.tagId || ''} allowEmpty emptyLabel="No tag" onChange={(v) => setA({ tagId: v || undefined })} options={(tags || []).map((t) => ({ value: t.id, label: t.name }))} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Switch label="Mark it as read" checked={!!a.markRead} onChange={(v) => setA({ markRead: v || undefined })} />
          <Switch label="Flag it" checked={!!a.flag} onChange={(v) => setA({ flag: v || undefined })} />
        </div>
        <Switch label="Then stop" hint="Later rules do not run on mail this rule caught." checked={form.stopHere} onChange={(v) => setForm({ ...form, stopHere: v })} />
      </fieldset>
      <div className="flex gap-2 justify-end"><Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button><Button type="submit" loading={saving} disabled={!form.name.trim()}>Save rule</Button></div>
    </form>
  );
}

// ── Quick steps ───────────────────────────────────────────────────────────────────────────────
const STEPS = [['read', 'Mark as read'], ['unread', 'Mark as unread'], ['flag', 'Flag'], ['pin', 'Pin'], ['tag', 'Tag…'], ['move', 'Move to…'], ['archive', 'Archive'], ['delete', 'Delete']];
const stepLabel = (s, accounts, tags) => (s.type === 'move' ? `Move to ${folderName(accounts, s.folderId)}` : s.type === 'tag' ? `Tag ${tagName(tags, s.tagId)}` : (STEPS.find((x) => x[0] === s.type) || [])[1]);

export function QuickStepsSection({ accounts, tags, onChanged }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/mail/quicksteps').then((r) => setList(r.data || [])).catch((e) => { setList([]); toast.error(errMsg(e)); }), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const b = { name: form.name, steps: form.steps };
      if (form.id) await api.put(`/mail/quicksteps/${form.id}`, b); else await api.post('/mail/quicksteps', b);
      toast.success('Quick step saved. It is on the ribbon under Quick steps.'); setForm(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove the quick step "${s.name}"?`)) return;
    try { await api.delete(`/mail/quicksteps/${s.id}`); load(); onChanged && onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const folders = folderOptions(accounts);
  const setStep = (i, patch) => setForm({ ...form, steps: form.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  return (
    <>
      <Note>A quick step does several things to the selected mail in one click, such as "tag Plan review, mark read and move to Plans". Steps run in order; a move, archive or delete ends it. A "Move to" step moves only mail in that folder's mailbox.</Note>
      <div className="mt-4 flex justify-end"><Button size="sm" icon={Plus} onClick={() => setForm({ name: '', steps: [{ type: 'read' }] })}>New quick step</Button></div>
      {form && (
        <form className="mt-3 rounded-xl ring-1 ring-sky-200 bg-sky-50/40 p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={60} autoFocus placeholder="e.g. File plan review" /></Field>
          <ol className="space-y-2">
            {form.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-6 h-10 flex items-center justify-center text-[12px] font-semibold text-slate-500">{i + 1}</span>
                <div className="flex-1 grid sm:grid-cols-2 gap-2">
                  <Select value={s.type} onChange={(v) => setStep(i, { type: v, folderId: undefined, tagId: undefined })} options={STEPS.map(([v, l]) => ({ value: v, label: l }))} />
                  {s.type === 'move' && <Select value={s.folderId || ''} placeholder="Choose the folder" onChange={(v) => setStep(i, { folderId: v })} options={folders} />}
                  {s.type === 'tag' && <Select value={s.tagId || ''} placeholder="Choose the tag" onChange={(v) => setStep(i, { tagId: v })} options={(tags || []).map((t) => ({ value: t.id, label: t.name }))} />}
                </div>
                <IconAct label="Remove this step" icon={X} onClick={() => setForm({ ...form, steps: form.steps.filter((_, j) => j !== i) })} />
              </li>
            ))}
          </ol>
          {form.steps.length < 10 && <Button size="sm" variant="secondary" icon={ArrowDown} onClick={() => setForm({ ...form, steps: [...form.steps, { type: 'flag' }] })}>Add a step</Button>}
          <div className="flex gap-2 justify-end"><Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button><Button type="submit" loading={busy} disabled={!form.name.trim() || !form.steps.length}>Save quick step</Button></div>
        </form>
      )}
      {list === null ? <Empty>Loading…</Empty> : list.length ? (
        <ul className="mt-3 rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
          {list.map((s) => (
            <li key={s.id} className="flex items-start gap-3 px-3 py-2.5">
              <Zap size={15} className="text-[#0f6cbd] mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-slate-800">{s.name}</p>
                <p className="text-[12px] text-slate-500">{(s.steps || []).map((x) => stepLabel(x, accounts, tags)).join(' → ')}</p>
              </div>
              <IconAct label="Change" icon={Pencil} onClick={() => setForm({ id: s.id, name: s.name, steps: s.steps || [] })} />
              <IconAct label="Remove" icon={Trash2} danger onClick={() => remove(s)} />
            </li>
          ))}
        </ul>
      ) : !form && <Empty>No quick steps yet.</Empty>}
    </>
  );
}
